import React, { useEffect, useState, useMemo, useCallback } from 'react';
import api from '../services/api';
import {
    Box, Typography, Container, Paper, Grid, CircularProgress, Fade, Grow,
    MenuItem, Select, FormControl, InputLabel, Collapse, IconButton, Chip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    LinearProgress, Tooltip, Button, Alert, Checkbox
} from '@mui/material';
import {
    ExpandMore, ExpandLess, TrendingUp, TrendingDown, AccountBalance,
    CalendarMonth, Category as CategoryIcon, Receipt, Sync, AutoFixHigh,
    List as ListIcon
} from '@mui/icons-material';
import { PERSONAL_CATEGORIES, PERSONAL_CAT_MAP, PERSONAL_GROUPS } from '../constants/personalCategories';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const GROUP_COLORS = {
    Essentials:      { bg: '#e3f2fd', bar: '#1976d2', text: '#0d47a1' },
    Transportation:  { bg: '#e8f5e9', bar: '#388e3c', text: '#1b5e20' },
    'Food & Drink':  { bg: '#fff3e0', bar: '#f57c00', text: '#e65100' },
    Health:          { bg: '#fce4ec', bar: '#d81b60', text: '#880e4f' },
    Family:          { bg: '#f3e5f5', bar: '#8e24aa', text: '#4a148c' },
    Shopping:        { bg: '#e8eaf6', bar: '#5c6bc0', text: '#283593' },
    Entertainment:   { bg: '#fff8e1', bar: '#ffa000', text: '#ff6f00' },
    Travel:          { bg: '#e0f7fa', bar: '#00acc1', text: '#006064' },
    Other:           { bg: '#f5f5f5', bar: '#78909c', text: '#37474f' },
    Income:          { bg: '#e8f5e9', bar: '#43a047', text: '#1b5e20' },
    'Non-Expense':   { bg: '#efebe9', bar: '#8d6e63', text: '#3e2723' },
    _uncategorized:  { bg: '#fafafa', bar: '#bdbdbd', text: '#616161' },
};

const fmt = (v) => parseFloat(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function PersonalExpenses() {
    const [year, setYear] = useState(new Date().getFullYear() - 1);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expandedMonth, setExpandedMonth] = useState(null);
    const [expandedGroup, setExpandedGroup] = useState(null);
    const [backfilling, setBackfilling] = useState(false);
    const [backfillResult, setBackfillResult] = useState(null);
    const [showTxns, setShowTxns] = useState(false);
    const [txns, setTxns] = useState([]);
    const [txnLoading, setTxnLoading] = useState(false);
    const [txnMonth, setTxnMonth] = useState(0);
    const [txnAccount, setTxnAccount] = useState('');
    const [selected, setSelected] = useState(new Set());
    const [aiCategorizing, setAiCategorizing] = useState(false);
    const [accounts, setAccounts] = useState([]);

    const loadData = useCallback((yr, silent = false) => {
        if (!silent) { setLoading(true); setExpandedMonth(null); }
        return api.get('/api/expenses/monthly', { params: { year: yr } })
            .then(res => setData(res.data))
            .catch(err => console.error('Failed to load monthly expenses:', err))
            .finally(() => { if (!silent) setLoading(false); });
    }, []);

    useEffect(() => { loadData(year); }, [year, loadData]);

    useEffect(() => {
        api.get('/api/personal-finance/accounts')
            .then(res => setAccounts(res.data || []))
            .catch(() => {});
    }, []);

    const loadTxns = useCallback(() => {
        setTxnLoading(true);
        const params = { year };
        if (txnMonth > 0) params.month = txnMonth;
        if (txnAccount) params.accountId = txnAccount;
        api.get('/api/expenses/transactions', { params })
            .then(res => { setTxns(res.data || []); setSelected(new Set()); })
            .catch(err => console.error('Failed to load transactions:', err))
            .finally(() => setTxnLoading(false));
    }, [year, txnMonth, txnAccount]);

    useEffect(() => { if (showTxns && txnMonth > 0) loadTxns(); }, [showTxns, loadTxns, txnMonth]);

    const handleCategoryChange = useCallback(async (id, code) => {
        try {
            await api.put(`/api/expenses/transactions/${id}/category`, { expenseCategory: code || null });
            setTxns(prev => prev.map(t => t.id === id ? { ...t, expenseCategory: code || null } : t));
            await loadData(year, true);
        } catch (err) {
            console.error('Failed to update category:', err);
        }
    }, [year, loadData]);

    const toggleSelect = (id) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selected.size === filteredTxns.length) setSelected(new Set());
        else setSelected(new Set(filteredTxns.map(t => t.id)));
    };

    const handleAiCategorize = async () => {
        let ids = [...selected];
        if (ids.length === 0) {
            ids = filteredTxns.filter(t => !t.expenseCategory).map(t => t.id);
        }
        if (ids.length === 0) return;
        setAiCategorizing(true);
        try {
            const res = await api.post('/api/expenses/auto-categorize', ids.map(id => ({ id })));
            const results = res.data.results || {};
            setTxns(prev => prev.map(t => results[t.id] ? { ...t, expenseCategory: results[t.id] } : t));
            setSelected(new Set());
            await loadData(year, true);
        } catch (err) {
            console.error('AI categorize failed:', err);
        } finally {
            setAiCategorizing(false);
        }
    };

    const filteredTxns = useMemo(() => txns, [txns]);

    const handleBackfill = async () => {
        setBackfilling(true);
        setBackfillResult(null);
        try {
            const res = await api.post('/api/expenses/backfill');
            setBackfillResult(res.data);
            loadData(year);
        } catch (err) {
            console.error('Backfill failed:', err);
            setBackfillResult({ error: true });
        } finally {
            setBackfilling(false);
        }
    };

    const catLookup = useMemo(() => {
        const m = {};
        PERSONAL_CATEGORIES.forEach(c => { m[c.code] = c; });
        return m;
    }, []);

    const resolveCategory = (code) => {
        if (code === '_uncategorized') return { label: 'Uncategorized', group: '_uncategorized' };
        const c = catLookup[code];
        return c || { label: code, group: 'Other' };
    };

    const yearlyByGroup = useMemo(() => {
        if (!data?.yearlyCategories) return [];
        const groupMap = {};
        Object.entries(data.yearlyCategories).forEach(([code, amt]) => {
            const cat = resolveCategory(code);
            const g = cat.group;
            if (!groupMap[g]) groupMap[g] = { group: g, total: 0, categories: {} };
            groupMap[g].total += parseFloat(amt);
            groupMap[g].categories[code] = (groupMap[g].categories[code] || 0) + parseFloat(amt);
        });
        return Object.values(groupMap).sort((a, b) => b.total - a.total);
        // eslint-disable-next-line
    }, [data, catLookup]);

    const monthlyByGroup = useMemo(() => {
        if (!data?.months) return [];
        const groupMap = {};
        const monthsArr = Array.from(data.months);
        monthsArr.forEach((mData) => {
            const mi = mData.month - 1;
            const cats = mData.categories || {};
            Object.entries(cats).forEach(([code, amt]) => {
                const cat = resolveCategory(code);
                const g = cat.group;
                if (!groupMap[g]) groupMap[g] = { group: g, total: 0, monthly: new Array(12).fill(0), categories: {} };
                const a = parseFloat(amt);
                groupMap[g].total += a;
                groupMap[g].monthly[mi] += a;
                if (!groupMap[g].categories[code]) groupMap[g].categories[code] = { total: 0, monthly: new Array(12).fill(0) };
                groupMap[g].categories[code].total += a;
                groupMap[g].categories[code].monthly[mi] += a;
            });
        });
        return Object.values(groupMap).sort((a, b) => b.total - a.total);
        // eslint-disable-next-line
    }, [data, catLookup]);

    const months = useMemo(() => {
        if (!data?.months) return [];
        return Array.from(data.months);
    }, [data]);

    const maxMonth = useMemo(() => {
        return months.reduce((mx, m) => Math.max(mx, parseFloat(m.total || 0)), 1);
    }, [months]);

    const yearlyTotal = data ? parseFloat(data.yearlyTotal || 0) : 0;
    const monthlyAvg = data ? parseFloat(data.monthlyAverage || 0) : 0;

    const highMonth = useMemo(() => {
        let best = null;
        months.forEach(m => {
            const t = parseFloat(m.total || 0);
            if (!best || t > best.total) best = { month: m.month, total: t };
        });
        return best;
    }, [months]);

    const lowMonth = useMemo(() => {
        let best = null;
        months.forEach(m => {
            const t = parseFloat(m.total || 0);
            if (t > 0 && (!best || t < best.total)) best = { month: m.month, total: t };
        });
        return best;
    }, [months]);

    if (loading) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'linear-gradient(135deg, #fafafa 0%, #f5f0ff 100%)' }}>
                <CircularProgress sx={{ color: '#667eea' }} />
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', pt: 2, pb: 8,
            background: 'linear-gradient(135deg, #fafafa 0%, #f5f0ff 100%)' }}>
            <Container maxWidth="xl">
                {/* Header */}
                <Fade in timeout={600}>
                    <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        boxShadow: '0 8px 32px rgba(102,126,234,0.3)' }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                            <Box>
                                <Typography variant="h5" fontWeight={800} color="white" gutterBottom>
                                    Personal Expense Tracker
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                    Monthly breakdown of personal spending across all accounts
                                </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={1.5}>
                                <FormControl size="small" sx={{ minWidth: 100 }}>
                                    <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Year</InputLabel>
                                    <Select value={year} label="Year" onChange={e => setYear(e.target.value)}
                                        sx={{ color: '#fff', fontWeight: 700,
                                            '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.4)' },
                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.7)' },
                                            '.MuiSvgIcon-root': { color: '#fff' } }}>
                                        {[2026, 2025, 2024, 2023].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <Tooltip title="Sync existing transactions from Personal Finance into Expenses" arrow>
                                    <Button variant="outlined" size="small"
                                        startIcon={backfilling ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Sync />}
                                        onClick={handleBackfill} disabled={backfilling}
                                        sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)', textTransform: 'none',
                                            fontWeight: 600, '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' } }}>
                                        {backfilling ? 'Syncing...' : 'Sync'}
                                    </Button>
                                </Tooltip>
                            </Box>
                        </Box>
                        {backfillResult && !backfillResult.error && (
                            <Alert severity="success" sx={{ mt: 1.5, borderRadius: 2 }}
                                onClose={() => setBackfillResult(null)}>
                                Synced {backfillResult.created} new transactions ({backfillResult.skipped} already existed)
                            </Alert>
                        )}
                        {backfillResult?.error && (
                            <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}
                                onClose={() => setBackfillResult(null)}>
                                Sync failed. Please try again.
                            </Alert>
                        )}
                    </Paper>
                </Fade>

                {yearlyTotal === 0 && !loading && (
                    <Fade in timeout={600}>
                        <Paper elevation={0} sx={{ p: 4, mb: 3, borderRadius: 3, textAlign: 'center',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
                            <Receipt sx={{ fontSize: 48, color: '#bdbdbd', mb: 1 }} />
                            <Typography variant="h6" fontWeight={700} color="text.secondary" gutterBottom>
                                No expense data for {year}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Click <strong>Sync</strong> above to import existing transactions from Personal Finance, or upload new statements on the Personal page.
                            </Typography>
                        </Paper>
                    </Fade>
                )}

                {/* Summary Cards */}
                <Grid container spacing={2.5} sx={{ mb: 3 }}>
                    {[
                        { label: 'Total Expenses', value: `$${fmt(yearlyTotal)}`, icon: <Receipt />,
                          color: '#667eea', bg: 'linear-gradient(135deg,#667eea,#764ba2)' },
                        { label: 'Monthly Average', value: `$${fmt(monthlyAvg)}`, icon: <CalendarMonth />,
                          color: '#11998e', bg: 'linear-gradient(135deg,#11998e,#38ef7d)' },
                        { label: 'Highest Month', value: highMonth ? `${MONTHS[highMonth.month - 1]}: $${fmt(highMonth.total)}` : '—',
                          icon: <TrendingUp />, color: '#ee0979', bg: 'linear-gradient(135deg,#ee0979,#ff6a00)' },
                        { label: 'Lowest Month', value: lowMonth ? `${MONTHS[lowMonth.month - 1]}: $${fmt(lowMonth.total)}` : '—',
                          icon: <TrendingDown />, color: '#36d1dc', bg: 'linear-gradient(135deg,#36d1dc,#5b86e5)' },
                        { label: 'Accounts', value: data?.yearlyAccounts ? Object.keys(data.yearlyAccounts).length : 0,
                          icon: <AccountBalance />, color: '#8e24aa', bg: 'linear-gradient(135deg,#8e24aa,#ce93d8)' },
                        { label: 'Categories Used', value: data?.yearlyCategories ? Object.keys(data.yearlyCategories).length : 0,
                          icon: <CategoryIcon />, color: '#f57c00', bg: 'linear-gradient(135deg,#f57c00,#ffb74d)' },
                    ].map((card, i) => (
                        <Grid item xs={6} md={2} key={i}>
                            <Grow in timeout={400 + i * 100}>
                                <Paper elevation={0} sx={{ p: 2, borderRadius: 3, textAlign: 'center',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.06)', transition: 'transform 0.2s',
                                    '&:hover': { transform: 'translateY(-4px)' } }}>
                                    <Box sx={{ width: 40, height: 40, borderRadius: 2, mx: 'auto', mb: 1,
                                        background: card.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        {React.cloneElement(card.icon, { sx: { color: '#fff', fontSize: 22 } })}
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" display="block">{card.label}</Typography>
                                    <Typography variant="subtitle2" fontWeight={700}>{card.value}</Typography>
                                </Paper>
                            </Grow>
                        </Grid>
                    ))}
                </Grid>

                {/* Monthly Bar Chart */}
                <Fade in timeout={800}>
                    <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
                        <Typography variant="h6" fontWeight={700} sx={{ mb: 2.5, color: '#333' }}>
                            Monthly Spending
                        </Typography>
                        <Box display="flex" alignItems="flex-end" gap={1} sx={{ height: 220, px: 1 }}>
                            {months.map((m, i) => {
                                const total = parseFloat(m.total || 0);
                                const pct = maxMonth > 0 ? (total / maxMonth) * 100 : 0;
                                const isExpanded = expandedMonth === m.month;
                                const isAboveAvg = total > monthlyAvg;
                                return (
                                    <Tooltip key={i} title={`${MONTHS[i]}: $${fmt(total)}`} arrow>
                                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
                                            cursor: 'pointer' }}
                                            onClick={() => setExpandedMonth(isExpanded ? null : m.month)}>
                                            <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, color: '#555', fontSize: '0.65rem' }}>
                                                ${total >= 1000 ? `${(total / 1000).toFixed(1)}k` : Math.round(total)}
                                            </Typography>
                                            <Box sx={{
                                                width: '100%', borderRadius: '8px 8px 0 0',
                                                height: `${Math.max(pct, 2)}%`,
                                                minHeight: 4,
                                                background: isExpanded
                                                    ? 'linear-gradient(180deg, #764ba2, #667eea)'
                                                    : isAboveAvg
                                                        ? 'linear-gradient(180deg, #ee0979, #ff6a00)'
                                                        : 'linear-gradient(180deg, #667eea, #a8c0ff)',
                                                transition: 'all 0.3s ease',
                                                boxShadow: isExpanded ? '0 -4px 12px rgba(118,75,162,0.4)' : 'none',
                                                '&:hover': { opacity: 0.85, transform: 'scaleY(1.03)' }
                                            }} />
                                            <Typography variant="caption" sx={{
                                                mt: 0.5, fontWeight: isExpanded ? 800 : 600,
                                                color: isExpanded ? '#764ba2' : '#888', fontSize: '0.7rem'
                                            }}>
                                                {MONTHS[i]}
                                            </Typography>
                                        </Box>
                                    </Tooltip>
                                );
                            })}
                        </Box>
                        {monthlyAvg > 0 && (
                            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ flex: 1, height: 1, borderTop: '2px dashed rgba(102,126,234,0.3)' }} />
                                <Typography variant="caption" color="text.secondary">
                                    Avg: ${fmt(monthlyAvg)}/mo
                                </Typography>
                                <Box sx={{ flex: 1, height: 1, borderTop: '2px dashed rgba(102,126,234,0.3)' }} />
                            </Box>
                        )}
                    </Paper>
                </Fade>

                {/* Expanded Month Detail */}
                <Collapse in={expandedMonth !== null}>
                    {expandedMonth && (() => {
                        const mData = months.find(m => m.month === expandedMonth);
                        if (!mData) return null;
                        const cats = mData.categories || {};
                        const accts = mData.accounts || {};
                        const mTotal = parseFloat(mData.total || 0);
                        const catEntries = Object.entries(cats)
                            .map(([code, amt]) => ({ code, ...resolveCategory(code), amount: parseFloat(amt) }))
                            .sort((a, b) => b.amount - a.amount);
                        const acctEntries = Object.entries(accts)
                            .map(([name, amt]) => ({ name, amount: parseFloat(amt) }))
                            .sort((a, b) => b.amount - a.amount);

                        return (
                            <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                                borderLeft: '4px solid #764ba2' }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Typography variant="h6" fontWeight={700} color="#764ba2">
                                        {MONTHS[expandedMonth - 1]} {year} — ${fmt(mTotal)}
                                    </Typography>
                                    <IconButton size="small" onClick={() => setExpandedMonth(null)}>
                                        <ExpandLess />
                                    </IconButton>
                                </Box>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={7}>
                                        <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 1.5 }}>
                                            By Category
                                        </Typography>
                                        {catEntries.map((c, i) => {
                                            const pct = mTotal > 0 ? (c.amount / mTotal) * 100 : 0;
                                            const colors = GROUP_COLORS[c.group] || GROUP_COLORS._uncategorized;
                                            return (
                                                <Box key={i} sx={{ mb: 1.2 }}>
                                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.3}>
                                                        <Box display="flex" alignItems="center" gap={1}>
                                                            <Chip label={c.group} size="small" sx={{
                                                                height: 18, fontSize: '0.6rem', fontWeight: 700,
                                                                backgroundColor: colors.bg, color: colors.text }} />
                                                            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                                                                {c.label}
                                                            </Typography>
                                                        </Box>
                                                        <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem' }}>
                                                            ${fmt(c.amount)} <Typography component="span" variant="caption" color="text.secondary">
                                                                ({pct.toFixed(1)}%)
                                                            </Typography>
                                                        </Typography>
                                                    </Box>
                                                    <LinearProgress variant="determinate" value={pct}
                                                        sx={{ height: 6, borderRadius: 3, backgroundColor: colors.bg,
                                                            '& .MuiLinearProgress-bar': { backgroundColor: colors.bar, borderRadius: 3 } }} />
                                                </Box>
                                            );
                                        })}
                                    </Grid>
                                    <Grid item xs={12} md={5}>
                                        <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 1.5 }}>
                                            By Account
                                        </Typography>
                                        {acctEntries.map((a, i) => {
                                            const pct = mTotal > 0 ? (a.amount / mTotal) * 100 : 0;
                                            return (
                                                <Box key={i} sx={{ mb: 1.2 }}>
                                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.3}>
                                                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                                                            {a.name}
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem' }}>
                                                            ${fmt(a.amount)} <Typography component="span" variant="caption" color="text.secondary">
                                                                ({pct.toFixed(1)}%)
                                                            </Typography>
                                                        </Typography>
                                                    </Box>
                                                    <LinearProgress variant="determinate" value={pct}
                                                        sx={{ height: 6, borderRadius: 3, backgroundColor: '#e3f2fd',
                                                            '& .MuiLinearProgress-bar': { backgroundColor: '#1976d2', borderRadius: 3 } }} />
                                                </Box>
                                            );
                                        })}
                                    </Grid>
                                </Grid>
                            </Paper>
                        );
                    })()}
                </Collapse>

                {/* Monthly Category Breakdown */}
                <Fade in timeout={1000}>
                    <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
                        <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: '#333' }}>
                            Category Breakdown — Monthly
                        </Typography>
                        <TableContainer sx={{ borderRadius: 2, border: '1px solid rgba(0,0,0,0.08)' }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', backgroundColor: '#f8f9fa',
                                            position: 'sticky', left: 0, zIndex: 3, minWidth: 180 }}>
                                            Category
                                        </TableCell>
                                        {MONTHS.map((m) => (
                                            <TableCell key={m} align="right" sx={{ fontWeight: 700, fontSize: '0.7rem',
                                                backgroundColor: '#f8f9fa', minWidth: 75, whiteSpace: 'nowrap' }}>
                                                {m}
                                            </TableCell>
                                        ))}
                                        <TableCell align="right" sx={{ fontWeight: 800, fontSize: '0.75rem',
                                            backgroundColor: '#f0eef8', minWidth: 90 }}>
                                            Total
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {monthlyByGroup.map((g, gi) => {
                                        const colors = GROUP_COLORS[g.group] || GROUP_COLORS._uncategorized;
                                        const isOpen = expandedGroup === g.group;
                                        const catEntries = Object.entries(g.categories)
                                            .map(([code, d]) => ({ code, ...resolveCategory(code), ...d }))
                                            .sort((a, b) => b.total - a.total);

                                        return (
                                            <React.Fragment key={gi}>
                                                <TableRow hover sx={{ cursor: 'pointer', '&:hover': { backgroundColor: colors.bg } }}
                                                    onClick={() => setExpandedGroup(isOpen ? null : g.group)}>
                                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem',
                                                        position: 'sticky', left: 0, backgroundColor: '#fff', zIndex: 1,
                                                        borderLeft: `4px solid ${colors.bar}` }}>
                                                        <Box display="flex" alignItems="center" gap={0.5}>
                                                            {isOpen ? <ExpandLess sx={{ fontSize: 16, color: '#999' }} /> : <ExpandMore sx={{ fontSize: 16, color: '#999' }} />}
                                                            {g.group === '_uncategorized' ? 'Uncategorized' : g.group}
                                                        </Box>
                                                    </TableCell>
                                                    {g.monthly.map((v, mi) => (
                                                        <TableCell key={mi} align="right" sx={{ fontWeight: 600, fontSize: '0.75rem',
                                                            color: v > 0 ? '#333' : '#ccc' }}>
                                                            {v > 0 ? `$${fmt(v)}` : '–'}
                                                        </TableCell>
                                                    ))}
                                                    <TableCell align="right" sx={{ fontWeight: 800, fontSize: '0.8rem',
                                                        backgroundColor: 'rgba(102,126,234,0.04)' }}>
                                                        ${fmt(g.total)}
                                                    </TableCell>
                                                </TableRow>
                                                {isOpen && catEntries.map((c, ci) => (
                                                    <TableRow key={`${gi}-${ci}`} sx={{ backgroundColor: 'rgba(0,0,0,0.015)' }}>
                                                        <TableCell sx={{ fontSize: '0.75rem', pl: 5,
                                                            position: 'sticky', left: 0, backgroundColor: '#fafafa', zIndex: 1,
                                                            borderLeft: `4px solid transparent`, color: '#555' }}>
                                                            {c.label}
                                                        </TableCell>
                                                        {c.monthly.map((v, mi) => (
                                                            <TableCell key={mi} align="right" sx={{ fontSize: '0.7rem',
                                                                color: v > 0 ? '#555' : '#ddd' }}>
                                                                {v > 0 ? `$${fmt(v)}` : '–'}
                                                            </TableCell>
                                                        ))}
                                                        <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem',
                                                            backgroundColor: 'rgba(0,0,0,0.02)' }}>
                                                            ${fmt(c.total)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </React.Fragment>
                                        );
                                    })}
                                    <TableRow sx={{ backgroundColor: 'rgba(102,126,234,0.06)' }}>
                                        <TableCell sx={{ fontWeight: 800, fontSize: '0.8rem',
                                            position: 'sticky', left: 0, backgroundColor: 'rgba(102,126,234,0.06)', zIndex: 1 }}>
                                            Total
                                        </TableCell>
                                        {months.map((m, i) => (
                                            <TableCell key={i} align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                                                ${fmt(parseFloat(m.total || 0))}
                                            </TableCell>
                                        ))}
                                        <TableCell align="right" sx={{ fontWeight: 800, fontSize: '0.85rem',
                                            backgroundColor: 'rgba(102,126,234,0.1)' }}>
                                            ${fmt(yearlyTotal)}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Fade>

                {/* Account Summary & Monthly Totals */}
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Fade in timeout={1000}>
                            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
                                <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: '#333' }}>
                                    By Account — Full Year
                                </Typography>
                                {data?.yearlyAccounts && Object.entries(data.yearlyAccounts)
                                    .map(([name, amt]) => ({ name, amount: parseFloat(amt) }))
                                    .sort((a, b) => b.amount - a.amount)
                                    .map((a, i) => {
                                        const pct = yearlyTotal > 0 ? (a.amount / yearlyTotal) * 100 : 0;
                                        return (
                                            <Box key={i} sx={{ mb: 1.5 }}>
                                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.3}>
                                                    <Typography variant="body2" fontWeight={600}>{a.name}</Typography>
                                                    <Box display="flex" alignItems="center" gap={0.5}>
                                                        <Typography variant="body2" fontWeight={700}>${fmt(a.amount)}</Typography>
                                                        <Typography variant="caption" color="text.secondary">({pct.toFixed(1)}%)</Typography>
                                                    </Box>
                                                </Box>
                                                <LinearProgress variant="determinate" value={pct}
                                                    sx={{ height: 8, borderRadius: 4, backgroundColor: '#f0f0f0',
                                                        '& .MuiLinearProgress-bar': {
                                                            borderRadius: 4,
                                                            background: `linear-gradient(90deg, #667eea, #764ba2)`
                                                        } }} />
                                            </Box>
                                        );
                                    })}
                            </Paper>
                        </Fade>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Fade in timeout={1200}>
                            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
                                <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: '#333' }}>
                                    Monthly Totals
                                </Typography>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Month</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Amount</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>vs Avg</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {months.map((m, i) => {
                                                const total = parseFloat(m.total || 0);
                                                const diff = monthlyAvg > 0 ? ((total - monthlyAvg) / monthlyAvg) * 100 : 0;
                                                return (
                                                    <TableRow key={i} hover sx={{ cursor: 'pointer',
                                                        backgroundColor: expandedMonth === m.month ? 'rgba(102,126,234,0.06)' : 'inherit',
                                                        '&:last-child td': { borderBottom: 0 } }}
                                                        onClick={() => setExpandedMonth(expandedMonth === m.month ? null : m.month)}>
                                                        <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                                                            {MONTHS[i]}
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                                                            ${fmt(total)}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {total > 0 && (
                                                                <Chip
                                                                    label={`${diff >= 0 ? '+' : ''}${diff.toFixed(0)}%`}
                                                                    size="small"
                                                                    sx={{
                                                                        height: 20, fontSize: '0.65rem', fontWeight: 700,
                                                                        backgroundColor: diff > 10 ? '#ffebee' : diff < -10 ? '#e8f5e9' : '#f5f5f5',
                                                                        color: diff > 10 ? '#c62828' : diff < -10 ? '#2e7d32' : '#666'
                                                                    }}
                                                                />
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                            <TableRow sx={{ backgroundColor: 'rgba(102,126,234,0.06)' }}>
                                                <TableCell sx={{ fontWeight: 800, fontSize: '0.8rem' }}>Total</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 800, fontSize: '0.8rem' }}>
                                                    ${fmt(yearlyTotal)}
                                                </TableCell>
                                                <TableCell />
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Fade>
                    </Grid>
                </Grid>

                {/* All Transactions Section */}
                <Fade in timeout={1000}>
                    <Paper elevation={0} sx={{ mt: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                        <Box onClick={() => { setShowTxns(prev => !prev); setTxns([]); setSelected(new Set()); }}
                            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2.5, cursor: 'pointer',
                                background: showTxns ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#fff',
                                color: showTxns ? '#fff' : 'text.primary', transition: 'all 0.3s ease',
                                '&:hover': { backgroundColor: showTxns ? undefined : 'rgba(102,126,234,0.04)' } }}>
                            <Box sx={{ p: 0.8, borderRadius: 2, backgroundColor: showTxns ? 'rgba(255,255,255,0.2)' : 'rgba(102,126,234,0.1)' }}>
                                <ListIcon sx={{ color: showTxns ? '#fff' : '#667eea', fontSize: 24 }} />
                            </Box>
                            <Box flex={1}>
                                <Typography variant="subtitle1" fontWeight={700}>All Transactions</Typography>
                                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                    View, filter, and categorize all your expense transactions
                                </Typography>
                            </Box>
                            {data?.uncategorizedCount > 0 && (
                                <Chip label={`${data.uncategorizedCount} uncategorized`} size="small"
                                    sx={{ fontWeight: 700, fontSize: '0.65rem', height: 22,
                                        backgroundColor: showTxns ? 'rgba(255,255,255,0.2)' : '#fff3e0',
                                        color: showTxns ? '#fff' : '#e65100' }} />
                            )}
                            {showTxns ? <ExpandLess /> : <ExpandMore />}
                        </Box>
                        <Collapse in={showTxns}>
                            <Box sx={{ p: 3, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                                {/* Filters */}
                                <Box display="flex" gap={1.5} alignItems="center" mb={2} flexWrap="wrap">
                                    <FormControl size="small" sx={{ minWidth: 120 }}>
                                        <InputLabel>Month</InputLabel>
                                        <Select value={txnMonth} label="Month" onChange={e => setTxnMonth(e.target.value)}
                                            sx={{ borderRadius: 2 }}
                                            displayEmpty>
                                            <MenuItem value={0} disabled><em>Select a month</em></MenuItem>
                                            {MONTHS.map((m, i) => <MenuItem key={i} value={i + 1}>{m}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                    <FormControl size="small" sx={{ minWidth: 160 }}>
                                        <InputLabel>Account</InputLabel>
                                        <Select value={txnAccount} label="Account" onChange={e => setTxnAccount(e.target.value)}
                                            sx={{ borderRadius: 2 }}>
                                            <MenuItem value="">All Accounts</MenuItem>
                                            {accounts.map(a => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                    <Box flex={1} />
                                    <Button variant="contained" size="small"
                                        startIcon={aiCategorizing ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <AutoFixHigh />}
                                        onClick={handleAiCategorize} disabled={aiCategorizing}
                                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2,
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            boxShadow: '0 4px 15px rgba(102,126,234,0.4)' }}>
                                        {aiCategorizing ? 'Categorizing...' : selected.size > 0
                                            ? `AI Categorize (${selected.size})`
                                            : `AI Categorize Uncategorized`}
                                    </Button>
                                    <Typography variant="caption" color="text.secondary">
                                        {filteredTxns.length} transactions
                                    </Typography>
                                </Box>

                                {txnMonth === 0 ? (
                                    <Box display="flex" flexDirection="column" alignItems="center" py={6} gap={1}>
                                        <CalendarMonth sx={{ fontSize: 40, color: '#bbb' }} />
                                        <Typography variant="body1" fontWeight={600} color="text.secondary">
                                            Select a month to view transactions
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Choose a month from the dropdown above to load transactions
                                        </Typography>
                                    </Box>
                                ) : txnLoading ? (
                                    <Box display="flex" justifyContent="center" py={4}>
                                        <CircularProgress size={28} sx={{ color: '#667eea' }} />
                                    </Box>
                                ) : (
                                    <TableContainer sx={{ maxHeight: 520, borderRadius: 2, border: '1px solid rgba(0,0,0,0.08)' }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell padding="checkbox" sx={{ backgroundColor: '#f8f9fa' }}>
                                                        <Checkbox size="small" checked={selected.size > 0 && selected.size === filteredTxns.length}
                                                            indeterminate={selected.size > 0 && selected.size < filteredTxns.length}
                                                            onChange={toggleSelectAll} />
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', backgroundColor: '#f8f9fa' }}>Date</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', backgroundColor: '#f8f9fa' }}>Description</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', backgroundColor: '#f8f9fa' }}>Account</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', backgroundColor: '#f8f9fa' }}>Amount</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', backgroundColor: '#f8f9fa', minWidth: 200 }}>Category</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {filteredTxns.map(tx => {
                                                    const cat = tx.expenseCategory ? PERSONAL_CAT_MAP[tx.expenseCategory] : null;
                                                    return (
                                                        <TableRow key={tx.id} hover
                                                            sx={{ backgroundColor: selected.has(tx.id) ? 'rgba(102,126,234,0.06)' : 'inherit' }}>
                                                            <TableCell padding="checkbox">
                                                                <Checkbox size="small" checked={selected.has(tx.id)}
                                                                    onChange={() => toggleSelect(tx.id)} />
                                                            </TableCell>
                                                            <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                                                                {tx.transactionDate ? new Date(tx.transactionDate + 'T00:00:00').toLocaleDateString() : '—'}
                                                            </TableCell>
                                                            <TableCell sx={{ fontSize: '0.8rem', maxWidth: 300 }}>
                                                                {tx.merchantName || tx.description || '—'}
                                                                {tx.merchantName && tx.description && tx.merchantName !== tx.description && (
                                                                    <Typography variant="caption" display="block" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                                                                        {tx.description.length > 60 ? tx.description.substring(0, 60) + '...' : tx.description}
                                                                    </Typography>
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip label={tx.account?.name || 'Unknown'} size="small"
                                                                    sx={{ height: 20, fontSize: '0.65rem', backgroundColor: '#f3e5f5', color: '#7b1fa2' }} />
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                                                ${parseFloat(tx.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Select
                                                                    size="small" displayEmpty
                                                                    value={tx.expenseCategory || ''}
                                                                    onChange={e => handleCategoryChange(tx.id, e.target.value)}
                                                                    sx={{ fontSize: '0.8rem', minWidth: 180,
                                                                        backgroundColor: tx.expenseCategory ? 'rgba(230,81,0,0.08)' : 'transparent',
                                                                        '& .MuiSelect-select': { py: 0.7 } }}
                                                                    renderValue={(val) => {
                                                                        if (!val) return <em style={{ color: '#999' }}>Select category...</em>;
                                                                        const c = PERSONAL_CAT_MAP[val];
                                                                        return c ? c.label : val;
                                                                    }}>
                                                                    <MenuItem value="" sx={{ color: '#999' }}><em>Uncategorized</em></MenuItem>
                                                                    {PERSONAL_GROUPS.map(group => [
                                                                        <MenuItem key={`hdr-${group}`} disabled
                                                                            sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase',
                                                                                color: '#e65100', backgroundColor: '#fff3e0', opacity: '1 !important' }}>
                                                                            {group}
                                                                        </MenuItem>,
                                                                        ...PERSONAL_CATEGORIES.filter(c => c.group === group).map(c => (
                                                                            <MenuItem key={c.code} value={c.code} sx={{ fontSize: '0.85rem', pl: 3 }}>
                                                                                {c.label}
                                                                            </MenuItem>
                                                                        ))
                                                                    ]).flat()}
                                                                </Select>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </Box>
                        </Collapse>
                    </Paper>
                </Fade>
            </Container>
        </Box>
    );
}

export default PersonalExpenses;
